/**
 * LoadingScreen for Poker Reborn
 *
 * The first screen players see - shows logo, loading progress, and status.
 * Rich's first impression on the world!
 *
 * @see docs/screens/loading/README.md - Loading flow, progress states, error handling
 */

import { Assets, Container, FillGradient, Graphics, Text, Sprite, Texture } from 'pixi.js';
import { ProgressBar } from '@/components/common/ProgressBar';
import { Button, PRIMARY_BUTTON_STYLE } from '@/components/common/Button';
import { appController } from '@/controllers/AppController';
import { getScaledValue } from '@/utils/responsive';
import type { ConnectionDiagnostic, DiagnosticType } from '@/types/network';

// =============================================================================
// ERROR TYPES
// =============================================================================

export type LoadingErrorType = 'connection' | 'credentials' | 'timeout' | 'server' | 'unknown';

interface ErrorConfig {
  icon: string;
  message: string;
  suggestion?: string; // Optional second line with actionable guidance
  canRetry: boolean;
}

const ERROR_CONFIGS: Record<LoadingErrorType, ErrorConfig> = {
  connection: {
    icon: '⚡',
    message: 'Unable to connect to server',
    suggestion: 'Check your internet and try again',
    canRetry: true,
  },
  credentials: {
    icon: '🔒',
    message: 'Invalid username or password',
    // No suggestion - cause is obvious
    canRetry: true,
  },
  timeout: {
    icon: '⏱',
    message: 'Connection timed out',
    suggestion: 'Check your internet and try again',
    canRetry: true,
  },
  server: {
    icon: '⚠',
    message: 'Server error',
    suggestion: 'Please try again later',
    canRetry: true,
  },
  unknown: {
    icon: '❌',
    message: 'An error occurred',
    suggestion: 'Please try again',
    canRetry: true,
  },
};

// =============================================================================
// LOADING SCREEN
// =============================================================================

export class LoadingScreen extends Container {
  private background: Graphics;
  private contentContainer: Container;
  private logoShadow: Graphics;
  private logoSprite: Sprite;
  private logoText: Text;
  private subtitleText: Text;
  private loadingStatusContainer: Container;
  private progressBar: ProgressBar;
  private statusText: Text;
  private versionText: Text;

  // Error display elements
  private errorContainer: Container;
  private errorBackground: Graphics;
  private errorIcon: Text;
  private errorMessage: Text;
  private suggestionContainer: Container;
  private suggestionBackground: Graphics;
  private suggestionIcon: Text;
  private suggestionText: Text;
  private retryButton: Button;

  private screenWidth: number;
  private screenHeight: number;

  // Error state
  private hasError = false;
  private hasSuggestion = false;

  // Callbacks
  public onRetry?: () => void;

  constructor(screenWidth: number, screenHeight: number) {
    super();

    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;

    // Create background
    this.background = new Graphics();
    this.addChild(this.background);

    // Create content container (groups logo, subtitle, progress, status)
    this.contentContainer = new Container();
    this.addChild(this.contentContainer);

    // Logo drop shadow (redrawn in layout for responsive sizing)
    this.logoShadow = new Graphics();
    this.contentContainer.addChild(this.logoShadow);

    // Create logo chip sprite (load async — sized in layout)
    this.logoSprite = new Sprite();
    this.logoSprite.anchor.set(0.5);
    this.logoSprite.visible = false;
    this.contentContainer.addChild(this.logoSprite);

    void Assets.load<Texture>('assets/images/logo.png').then((texture) => {
      this.logoSprite.texture = texture;
      this.logoSprite.visible = true;
      this.layout();
    });

    // Luxurious gold gradient for logo text — champagne to deep gold
    const logoGradient = new FillGradient({
      type: 'linear',
      start: { x: 0, y: 0 },
      end: { x: 0, y: 1 },
      colorStops: [
        { offset: 0, color: '#fff0c0' },
        { offset: 0.35, color: '#ffd700' },
        { offset: 0.65, color: '#daa520' },
        { offset: 1, color: '#8b6914' },
      ],
    });

    // Create logo text
    this.logoText = new Text({
      text: 'POKER REBORN',
      style: {
        fill: logoGradient,
        fontSize: 80,
        fontFamily: 'Arvo, Georgia, serif',
        fontWeight: 'bold',
        letterSpacing: 4,
        stroke: {
          color: '#3a2508',
          width: 2,
        },
        dropShadow: {
          color: '#0a0a1e',
          blur: 16,
          distance: 4,
          alpha: 0.8,
        },
      },
    });
    this.logoText.anchor.set(0.5);
    this.contentContainer.addChild(this.logoText);

    // Subtitle — soft silver-lavender, handwritten elegance
    const subtitleGradient = new FillGradient({
      type: 'linear',
      start: { x: 0, y: 0 },
      end: { x: 1, y: 0 },
      colorStops: [
        { offset: 0, color: '#9a8ab0' },
        { offset: 0.5, color: '#c8b8d8' },
        { offset: 1, color: '#9a8ab0' },
      ],
    });

    this.subtitleText = new Text({
      text: 'The Ultimate Poker Experience',
      style: {
        fill: subtitleGradient,
        fontSize: 24,
        fontFamily: 'Kalam, Roboto, Arial, sans-serif',
        fontWeight: 'bold',
        letterSpacing: 3,
        dropShadow: {
          color: '#06060e',
          blur: 8,
          distance: 0,
          alpha: 0.6,
        },
      },
    });
    this.subtitleText.anchor.set(0.5);
    this.contentContainer.addChild(this.subtitleText);

    // Create loading status container (progress bar + status text)
    this.loadingStatusContainer = new Container();
    this.addChild(this.loadingStatusContainer);

    // Create progress bar — gold theme to match branding
    this.progressBar = new ProgressBar({
      width: 400,
      height: 12,
      progress: 0,
      style: {
        fillColor: 0xdaa520,
        glowColor: 0xffd700,
        backgroundColor: 0x10101e,
        borderColor: 0x2a2a40,
        borderWidth: 1,
        cornerRadius: 6,
        glow: true,
      },
    });
    this.loadingStatusContainer.addChild(this.progressBar);

    // Status text — cool blue-grey
    this.statusText = new Text({
      text: 'Initializing...',
      style: {
        fill: 0x7a7a9a,
        fontSize: 13,
        fontFamily: 'Roboto, Arial, sans-serif',
        letterSpacing: 1,
      },
    });
    this.statusText.anchor.set(0.5);
    this.loadingStatusContainer.addChild(this.statusText);

    // Version text — barely visible, understated
    this.versionText = new Text({
      text: 'v1.0.0 - Rich Edition',
      style: {
        fill: 0x2a2a3e,
        fontSize: 11,
        fontFamily: 'Roboto, Arial, sans-serif',
        letterSpacing: 1,
      },
    });
    this.versionText.anchor.set(0.5);
    this.addChild(this.versionText);

    // Create error container (hidden by default)
    this.errorContainer = new Container();
    this.errorContainer.visible = false;
    this.addChild(this.errorContainer);

    // Error background (semi-transparent dark box)
    this.errorBackground = new Graphics();
    this.errorContainer.addChild(this.errorBackground);

    // Error icon
    this.errorIcon = new Text({
      text: '⚠',
      style: {
        fill: 0xe8637a,
        fontSize: 48,
        fontFamily: 'Roboto, Arial, sans-serif',
      },
    });
    this.errorIcon.anchor.set(0.5);
    this.errorContainer.addChild(this.errorIcon);

    // Error message
    this.errorMessage = new Text({
      text: 'An error occurred',
      style: {
        fill: 0xe8637a,
        fontSize: 18,
        fontFamily: 'Roboto, Arial, sans-serif',
        fontWeight: 'bold',
        align: 'center',
      },
    });
    this.errorMessage.anchor.set(0.5);
    this.errorContainer.addChild(this.errorMessage);

    // Suggestion container (hidden by default)
    this.suggestionContainer = new Container();
    this.suggestionContainer.visible = false;
    this.errorContainer.addChild(this.suggestionContainer);

    // Suggestion background box
    this.suggestionBackground = new Graphics();
    this.suggestionContainer.addChild(this.suggestionBackground);

    // Suggestion lightbulb icon
    this.suggestionIcon = new Text({
      text: '💡',
      style: {
        fontSize: 14,
        fontFamily: 'Roboto, Arial, sans-serif',
      },
    });
    this.suggestionContainer.addChild(this.suggestionIcon);

    // Suggestion text
    this.suggestionText = new Text({
      text: '',
      style: {
        fill: 0x9a9ab0,
        fontSize: 13,
        fontFamily: 'Roboto, Arial, sans-serif',
        align: 'center',
      },
    });
    this.suggestionText.anchor.set(0.5);
    this.suggestionContainer.addChild(this.suggestionText);

    // Retry button
    this.retryButton = new Button({
      label: 'Try Again',
      width: 150,
      height: 44,
      style: PRIMARY_BUTTON_STYLE,
    });
    this.retryButton.onClick = () => {
      this.hideError();
      this.onRetry?.();
    };
    this.errorContainer.addChild(this.retryButton);

    // Initial layout
    this.layout();

    // Listen for load progress
    appController.on('loadProgress', ({ progress, status }) => {
      this.setProgress(progress, status);
    });
  }

  // ===========================================================================
  // PUBLIC API
  // ===========================================================================

  /** Update progress */
  setProgress(progress: number, status?: string): void {
    this.progressBar.setProgress(progress / 100);

    if (status) {
      this.statusText.text = status;
    }
  }

  /** Set status text */
  setStatus(status: string): void {
    this.statusText.text = status;
  }

  /** Resize handler */
  resize(screenWidth: number, screenHeight: number): void {
    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;
    this.layout();
  }

  /** Update animation */
  update(deltaTime: number): void {
    // Update progress bar animation
    this.progressBar.update(deltaTime);
  }

  /** Show error state with error type */
  showError(errorType: LoadingErrorType, customMessage?: string, customSuggestion?: string): void {
    const config = ERROR_CONFIGS[errorType];

    // Determine suggestion to show (custom overrides config)
    const suggestion = customSuggestion ?? config.suggestion;
    this.hasSuggestion = Boolean(suggestion);

    // Update error display
    this.errorIcon.text = config.icon;
    this.errorMessage.text = customMessage ?? config.message;
    this.retryButton.visible = config.canRetry;

    // Update suggestion (if any)
    if (this.hasSuggestion && suggestion) {
      this.suggestionText.text = suggestion;
      this.suggestionContainer.visible = true;
    } else {
      this.suggestionContainer.visible = false;
    }

    // Hide loading status, show error
    this.loadingStatusContainer.visible = false;
    this.errorContainer.visible = true;
    this.hasError = true;

    // Stop progress bar glow
    this.progressBar.setStyle({ glow: false });

    // Layout error elements (dynamic height based on suggestion)
    this.layoutError();
  }

  /** Hide error and restore loading state */
  hideError(): void {
    this.errorContainer.visible = false;
    this.hasError = false;
    this.hasSuggestion = false;
    this.suggestionContainer.visible = false;

    // Show loading status
    this.loadingStatusContainer.visible = true;

    // Reset status
    this.statusText.style.fill = 0x7a7a9a;
    this.statusText.text = 'Connecting...';
    this.progressBar.setProgress(0);
    this.progressBar.setStyle({ glow: true });
  }

  /** Parse error message and show appropriate error type */
  showErrorFromMessage(errorMessage: string): void {
    let errorType: LoadingErrorType = 'unknown';

    const lowerMessage = errorMessage.toLowerCase();

    if (lowerMessage.includes('timeout')) {
      errorType = 'timeout';
    } else if (
      lowerMessage.includes('bad_auth') ||
      lowerMessage.includes('not_auth') ||
      lowerMessage.includes('authentication failed') ||
      lowerMessage.includes('invalid')
    ) {
      errorType = 'credentials';
    } else if (
      lowerMessage.includes('connect') ||
      lowerMessage.includes('websocket') ||
      lowerMessage.includes('network')
    ) {
      errorType = 'connection';
    } else if (lowerMessage.includes('server')) {
      errorType = 'server';
    }

    this.showError(errorType, errorMessage);
  }

  /**
   * Show error with full diagnostic information
   * Maps DiagnosticType to LoadingErrorType and shows with custom message/suggestion
   */
  showDiagnostic(diagnostic: ConnectionDiagnostic): void {
    // Map diagnostic type to existing LoadingErrorType
    const typeMap: Record<DiagnosticType, LoadingErrorType> = {
      NO_NETWORK: 'connection',
      BACKEND_DOWN: 'server',
      VPN_PORT_BLOCKED: 'connection',
      WS_PROTOCOL_BLOCKED: 'connection',
      AUTH_FAILED: 'credentials',
      TIMEOUT: 'timeout',
      UNKNOWN: 'unknown',
    };

    const errorType = typeMap[diagnostic.type];
    this.showError(errorType, diagnostic.message, diagnostic.suggestion);

    // Log technical details to console for debugging
    if (diagnostic.details) {
      console.debug('[NetworkDiagnostic]', diagnostic.type, diagnostic.details);
    }
  }

  /** Show ready state */
  showReady(): void {
    this.statusText.style.fill = 0xdaa520;
    this.statusText.text = 'Ready! Entering lobby...';
    this.progressBar.setProgress(1);
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private layout(): void {
    const { screenWidth: w, screenHeight: h } = this;
    const centerX = w / 2;
    const centerY = h / 2;
    const smooth = { discrete: false };
    const stepped = { discrete: true };

    // --- Responsive sizes ---
    const logoSize = getScaledValue(w, { 375: 64, 720: 90, 1200: 120, 1920: 120 }, smooth);
    const shadowRadius = logoSize * 0.48;
    const logoFontSize = getScaledValue(w, { 375: 36, 720: 56, 1200: 80, 1920: 80 }, smooth);
    const logoLetterSpacing = getScaledValue(w, { 375: 1, 720: 2, 1200: 4, 1920: 4 }, smooth);
    const logoStrokeWidth = getScaledValue(w, { 375: 1, 720: 1.5, 1200: 2, 1920: 2 }, smooth);
    const subtitleFontSize = getScaledValue(w, { 375: 14, 720: 18, 1200: 24, 1920: 24 }, smooth);
    const subtitleLetterSpacing = getScaledValue(w, { 375: 1, 720: 2, 1200: 3, 1920: 3 }, smooth);
    const statusFontSize = getScaledValue(w, { 375: 11, 720: 12, 1200: 13, 1920: 13 }, stepped);
    const versionFontSize = getScaledValue(w, { 375: 9, 720: 10, 1200: 11, 1920: 11 }, stepped);
    const progressBarWidth = getScaledValue(w, { 375: 220, 720: 300, 1200: 400, 1920: 400 }, smooth);
    const progressBarHeight = getScaledValue(w, { 375: 8, 720: 10, 1200: 12, 1920: 12 }, smooth);

    // --- Responsive spacing (local coords inside contentContainer) ---
    const spriteY = getScaledValue(w, { 375: -55, 720: -75, 1200: -100, 1920: -100 }, smooth);
    const textY = getScaledValue(w, { 375: 15, 720: 28, 1200: 40, 1920: 40 }, smooth);
    const subtitleY = getScaledValue(w, { 375: 55, 720: 85, 1200: 125, 1920: 125 }, smooth);
    const statusGap = getScaledValue(w, { 375: 28, 720: 34, 1200: 40, 1920: 40 }, smooth);

    // Draw background
    this.background.clear();
    this.drawBackground();

    // Position contentContainer at screen center
    this.contentContainer.x = centerX;
    this.contentContainer.y = centerY - 20;

    // Logo shadow (redraw for responsive size)
    this.logoShadow.clear();
    this.logoShadow.circle(0, 0, shadowRadius);
    this.logoShadow.fill({ color: 0x000000, alpha: 0.4 });
    this.logoShadow.x = 4;
    this.logoShadow.y = spriteY + 4;

    // Logo sprite
    if (this.logoSprite.visible) {
      this.logoSprite.width = logoSize;
      this.logoSprite.height = logoSize;
    }
    this.logoSprite.x = 0;
    this.logoSprite.y = spriteY;

    // Logo text
    this.logoText.style.fontSize = logoFontSize;
    this.logoText.style.letterSpacing = logoLetterSpacing;
    this.logoText.style.stroke = { color: '#3a2508', width: logoStrokeWidth };
    this.logoText.x = 0;
    this.logoText.y = textY;

    // Subtitle
    this.subtitleText.style.fontSize = subtitleFontSize;
    this.subtitleText.style.letterSpacing = subtitleLetterSpacing;
    this.subtitleText.x = 0;
    this.subtitleText.y = subtitleY;

    // Loading status container — ensure minimum gap from contentContainer bottom
    const contentBottom = this.contentContainer.y + subtitleY + subtitleFontSize;
    const minGap = getScaledValue(w, { 375: 30, 720: 40, 1200: 50, 1920: 50 }, smooth);
    const statusTargetY = h * 0.64;
    const statusMinY = contentBottom + minGap;

    this.loadingStatusContainer.x = 0;
    this.loadingStatusContainer.y = Math.max(statusTargetY, statusMinY);

    // Progress bar (resize + center)
    this.progressBar.resize(progressBarWidth, progressBarHeight);
    this.progressBar.x = centerX - progressBarWidth / 2;
    this.progressBar.y = 0;

    // Status text
    this.statusText.style.fontSize = statusFontSize;
    this.statusText.x = centerX;
    this.statusText.y = statusGap;

    // Version text
    this.versionText.style.fontSize = versionFontSize;
    this.versionText.x = centerX;
    this.versionText.y = h - 24;

    // Layout error if visible
    if (this.hasError) {
      this.layoutError();
    }
  }

  private layoutError(): void {
    const { screenWidth: w, screenHeight: h } = this;
    const centerX = w / 2;
    const centerY = h / 2;
    const smooth = { discrete: false };

    // Responsive error box sizing
    const baseBoxWidth = getScaledValue(w, { 375: 280, 720: 340, 1200: 400, 1920: 440 }, smooth);
    const boxWidth = this.hasSuggestion ? baseBoxWidth * 1.1 : baseBoxWidth;
    const boxHeight = this.hasSuggestion
      ? getScaledValue(w, { 375: 190, 720: 215, 1200: 240, 1920: 240 }, smooth)
      : getScaledValue(w, { 375: 160, 720: 180, 1200: 200, 1920: 200 }, smooth);
    const errorIconSize = getScaledValue(w, { 375: 32, 720: 40, 1200: 48, 1920: 48 }, smooth);
    const errorMsgSize = getScaledValue(w, { 375: 14, 720: 16, 1200: 18, 1920: 18 }, smooth);

    // Error container position
    this.errorContainer.x = 0;
    this.errorContainer.y = 0;

    const boxX = centerX - boxWidth / 2;
    const boxY = centerY - boxHeight / 2 + 20;

    // Draw error background box
    this.errorBackground.clear();
    this.errorBackground.roundRect(boxX, boxY, boxWidth, boxHeight, 12);
    this.errorBackground.fill({ color: 0x0e0e1a, alpha: 0.95 });
    this.errorBackground.roundRect(boxX, boxY, boxWidth, boxHeight, 12);
    this.errorBackground.stroke({ color: 0xe8637a, width: 1, alpha: 0.3 });

    this.errorIcon.style.fontSize = errorIconSize;
    this.errorMessage.style.fontSize = errorMsgSize;

    if (this.hasSuggestion) {
      this.errorIcon.x = centerX;
      this.errorIcon.y = boxY + boxHeight * 0.17;

      this.errorMessage.x = centerX;
      this.errorMessage.y = boxY + boxHeight * 0.36;

      const suggestionWidth = boxWidth * 0.86;
      const suggestionHeight = 36;
      const suggestionX = centerX - suggestionWidth / 2;
      const suggestionY = boxY + boxHeight * 0.48;

      this.suggestionBackground.clear();
      this.suggestionBackground.roundRect(suggestionX, suggestionY, suggestionWidth, suggestionHeight, 8);
      this.suggestionBackground.fill({ color: 0x151520 });
      this.suggestionBackground.stroke({ color: 0x2a2a3e, width: 1 });

      this.suggestionIcon.x = suggestionX + 12;
      this.suggestionIcon.y = suggestionY + 10;

      this.suggestionText.x = centerX + 10;
      this.suggestionText.y = suggestionY + suggestionHeight / 2;

      this.retryButton.x = centerX - 75;
      this.retryButton.y = boxY + boxHeight * 0.77;
    } else {
      this.errorIcon.x = centerX;
      this.errorIcon.y = boxY + boxHeight * 0.25;

      this.errorMessage.x = centerX;
      this.errorMessage.y = boxY + boxHeight * 0.5;

      this.retryButton.x = centerX - 75;
      this.retryButton.y = boxY + boxHeight * 0.7;
    }
  }

  private drawBackground(): void {
    const { screenWidth: w, screenHeight: h } = this;
    const centerX = w / 2;
    const centerY = h / 2;

    // Deep blue-violet base (matches room gradient bottom)
    this.background.rect(0, 0, w, h);
    this.background.fill(0x06060e);

    // Inner violet glow — casino atmosphere
    const glowRadius = Math.min(w, h) * 0.55;
    for (let i = 24; i >= 0; i--) {
      const radius = (glowRadius * i) / 24;
      const alpha = 0.025 * (24 - i);
      this.background.circle(centerX, centerY - 20, radius);
      this.background.fill({ color: 0x1a1a3e, alpha });
    }

    // Subtle violet-rose accent — warm depth behind the logo
    const accentRadius = Math.min(w, h) * 0.3;
    for (let i = 16; i >= 0; i--) {
      const radius = (accentRadius * i) / 16;
      const alpha = 0.01 * (16 - i);
      this.background.circle(centerX, centerY - 40, radius);
      this.background.fill({ color: 0x2a1a3e, alpha });
    }

    // Vignette — dark edges for cinematic depth
    const vignetteRadius = Math.max(w, h) * 0.8;
    for (let i = 0; i < 12; i++) {
      const radius = vignetteRadius - (i * vignetteRadius * 0.06);
      const alpha = 0.015 * i;
      this.background.circle(centerX, centerY, radius);
      this.background.stroke({ color: 0x000000, width: vignetteRadius * 0.06, alpha });
    }
  }
}

// =============================================================================
// FACTORY
// =============================================================================

/** Create a loading screen */
export function createLoadingScreen(width: number, height: number): LoadingScreen {
  return new LoadingScreen(width, height);
}
